import { useCallback } from 'react';
import _, { assign, isEmpty, omit, reduce } from 'lodash';
import createFlowService from 'new/components/flows/service/createFlowService';
import { createResourceSetupService } from 'new/components/flows/types/replication/CreateReplicationFlow/ResourceSetup/service/createResourceSetupService';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import usePaging from 'new/state/paging/repository/usePaging';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import { ITERATION_TYPE_TO_FIELDS_MAP } from 'new/components/flows/configure/RestAPISource/RestAPISource.constants';
import { Field } from '@nexla/@types';
import { CONNECTORS_WITHOUT_PATH } from 'js/Constants';
import { isUiModeCode } from 'new/utils/uiMode';

export default function useSourceSetup(formData, flowType) {
  const { getMetaInfoById, getSourceConfigFields } = usePaging();
  const {
    apiConfig,
    sourceConfig,
    updateFormData,
    credentialId,
    selectedBucket,
    selectedConnection,
  } = formData;
  const flowService = createFlowService();
  const service = createResourceSetupService();

  const meta = getMetaInfoById(selectedConnection);
  const { connectionCategory } = meta;

  const setupInitialFormData = ({ source, sink }) => {
    const { selectedBucket, displayPath, effectivePath } = service.getBucketInfoFromConfig(
      source?.source_config,
    );

    const sourceCred = source?.data_credentials ?? source?.data_credentials_id;
    const resolvedCredentialId =
      typeof sourceCred === 'object' && sourceCred?.id != null
        ? sourceCred.id
        : (sourceCred ?? credentialId);

    updateFormData({
      source,
      sink,
      selectedBucket,
      credentialId: resolvedCredentialId,
      sourceConfig: {
        sourceName: source?.name,
        sourceDescription: source?.description,
        ...(source?.source_config ||
          flowService.createInitialSourceConfig({
            empty: true,
            sourceConfig,
            flowType,
            connectionCategory,
            sourceConfigFields: getSourceConfigFields(selectedConnection, flowType),
          })),
        ...(effectivePath ? { path: effectivePath } : {}),
        ...(displayPath ? { ['ui.display_path']: displayPath } : {}),
        ...(source?.flow_triggers?.length > 0 ? { flow_triggers: source?.flow_triggers } : {}),
      },
      selectedConnection: source?.connector_type || selectedConnection,
    });
  };

  const validate = (onError) => {
    const connectionFields = getSourceConfigFields(selectedConnection, flowType);

    if (isUiModeCode(sourceConfig) || isUiModeCode(apiConfig)) {
      return true;
    }

    if (!CONNECTORS_WITHOUT_PATH.includes(selectedConnection) && !selectedBucket) {
      switch (connectionCategory) {
        case CONNECTION_CATEGORY_FILE:
          onError('Please select a folder to scan.');
          return;

        case CONNECTION_CATEGORY_NOSQL:
          onError('Please select a collection to scan.');
          return;

        case CONNECTION_CATEGORY_DATABASE:
          if (!sourceConfig.db_query_mode || sourceConfig.db_query_mode === 'Default') {
            onError('Please select a table to scan.');
            return;
          }

          break;
      }
    }

    let missingFields: Field[] = [];
    if (
      connectionCategory === CONNECTION_CATEGORY_REST ||
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API
    ) {
      const allStageAPIConfig = apiConfig['rest.iterations'];
      /** for each stage, validate required fields */
      allStageAPIConfig.forEach(async (stage: any) => {
        const { isPaginated } = stage;
        let fieldNames = ['url.template'];

        if (isPaginated) {
          const iterationType = stage['iteration.type'];
          fieldNames = fieldNames.concat(ITERATION_TYPE_TO_FIELDS_MAP[iterationType]);
        }

        const fieldsByName = connectionFields.reduce(
          (acc: any, field: Field) => ({ ...acc, [field.name]: field }),
          {},
        );

        const fields = _.map(fieldNames, (name) => fieldsByName[name]);
        missingFields = [...missingFields, ...getEmptyRequiredFields(fields, stage)];
      });
    } else {
      /**
       * - CONNECTION_CATEGORY_FILE
       * - CONNECTION_CATEGORY_NOSQL
       * - CONNECTION_CATEGORY_DATABASE
       */
      missingFields = getEmptyRequiredFields(connectionFields, sourceConfig);
    }

    if (!isEmpty(missingFields)) {
      const errorMessage = reduce(
        missingFields,
        (message, field) => `${message} ${field.display_name},`,
        'Please enter the following required fields: ',
      );
      onError(errorMessage);
      return false;
    }

    return true;
  };

  function onBucketSelected(selectedBucket) {
    const path = selectedBucket?.path;
    const displayPath = selectedBucket?.displayPath;
    const mimeType = selectedBucket?.mimeType;
    const idPath = selectedBucket?.idPath; // For leaf-ID-only connectors (Google Drive)

    const sourceConfigWithBucket = Object.assign({}, sourceConfig, {
      path,
      'ui.display_path': displayPath,
      'ui.selected_file_mime_type': mimeType,
      // Save the resolved ID path for leaf-ID-only connectors (Google Drive)
      // This allows future edits to skip iterative probing
      ...(idPath ? { 'ui.id_path': idPath } : {}),
    });

    updateFormData({
      selectedBucket,
      sourceConfig: sourceConfigWithBucket,
    });
  }

  const onTableSelected = useCallback(
    (table, database) => {
      const bucketObject = flowService.buildBucketObject(table, database);

      // if database or table changes, unset table-specific properties in
      // sourceConfig
      let newSourceConfig;

      if (
        sourceConfig &&
        table &&
        (table.name !== sourceConfig.table || table.database !== sourceConfig.database)
      ) {
        newSourceConfig = omit(sourceConfig, [
          'mode',
          'incrementing.column.name',
          'timestamp.column.name',
          'incrementing.load.from',
          'timestamp.load.from',
        ]);
      } else {
        newSourceConfig = sourceConfig;
      }

      let sourceConfigWithBucket = assign({}, newSourceConfig, bucketObject);

      updateFormData({
        selectedBucket: table,
        sourceConfig: sourceConfigWithBucket,
        table: table,
      });
    },
    [sourceConfig],
  );

  const cleanForm = (id) => {
    if (!!id) {
      updateFormData({
        credentialId: null,
        selectedCredentialInfo: null,
        connectionType: 'SINK',
        selectedConnection: null,
        selectedBucket: null,
        selectedTable: null,
        table: null,
        sourceConfig: null,
      });
    }
  };

  return {
    cleanForm,
    setupInitialFormData,
    onBucketSelected,
    validate,
    onTableSelected,
  };
}
